/**
 * PitchPrint Toast Preview System - CLEAN INTEGRATED VERSION
 * Selective control with no duplicate functions or conflicts
 */
(() => {
  'use strict';

  // CRITICAL: Only run on product pages
  const isProductPage = () => {
    return window.location.pathname.includes('/products/') || 
           document.querySelector('.product-form') || 
           document.querySelector('#option1');
  };

  if (!isProductPage()) {
    console.log('[ToastSystem] Not a product page, skipping initialization');
    return;
  }

  // Initialization guard
  let initializationComplete = false;
  let isActive = false;
  let widget = null;
  let currentProjectId = null;

  const stateManager = window.PitchPrintStateManager;
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[ToastSystem]', ...args);

  if (!stateManager) {
    console.error('[ToastSystem] StateManager not available - toast disabled');
    return;
  }

  // =====================================
  // TOAST MESSAGE CONFIGURATION
  // =====================================
  const TOAST_CONFIG = {
    // Master switch - set to false to disable ALL toasts
    enabled: true,
    
    // Individual message types - toggle each one independently
    messages: {
      projectSaved: true,        // "Design saved! Ready to add to cart"
      projectRestored: false,     // When returning to page with saved project
      projectCleared: false,      // "Project cleared. Please start designing again"
      addToCartSuccess: true,    // After successfully adding to cart
      variantChanged: false,      // When user changes product variants
      editingStarted: false,      // When user clicks "Edit Design"
      savingInProgress: false,    // While project is being saved
      errorOccurred: false,       // When errors happen
      manualTrigger: true         // Allow manual toast:activate events
    },
    
    // Appearance settings
    appearance: {
      position: 'top-right',      // top-right, top-left, bottom-right, bottom-left
      duration: 5000,             // Auto-hide after 5 seconds (0 = no auto-hide)
      showEditButton: false,      // Show "Edit Design" button
      showClearButton: false,     // Show "Clear Design" button  
      showAddToCartButton: false, // Show "Add to Cart" button
      allowDismiss: true          // Show X button to close
    }
  };

      // CRITICAL: Expose TOAST_CONFIG globally for unified system integration
      window.TOAST_CONFIG = TOAST_CONFIG;

  // Helper function
  const getActiveMessageTypes = () => {
    return Object.entries(TOAST_CONFIG.messages)
      .filter(([type, enabled]) => enabled)
      .map(([type]) => type);
  };

  // =====================================
  // SMART TOAST MESSAGE HANDLER
  // =====================================
  const showToastMessage = (messageType, options) => {
    if (!TOAST_CONFIG.enabled || !TOAST_CONFIG.messages[messageType]) {
      log(`🔇 Toast message '${messageType}' is disabled`);
      return;
    }

    log(`💬 Showing toast message: ${messageType}`);
    
    const toastData = {
      title: options.title || 'PitchPrint',
      message: options.message || '',
      type: options.type || 'success',
      projectId: options.projectId || null,
      messageType: messageType
    };

    activateToast(toastData);
  };

  // =====================================
  // ENHANCED WIDGET CREATION
  // =====================================
    const createWidget = (toastData = {}) => {
    if (!TOAST_CONFIG.enabled) {
      log('🔇 Widget creation skipped - system disabled');
      return;
    }

    if (widget && document.contains(widget)) {
      log('✅ Widget already exists');
      return;
    }

    // Remove any existing widgets first
    document.querySelectorAll('.unified-feedback-toast').forEach(el => el.remove());

    // Detect toast style from CSS variable
    const toastStyle = getComputedStyle(document.documentElement)
      .getPropertyValue('--toast-style')
      .trim() || 'floating';

    log(`🎨 Toast style: ${toastStyle}`);

    widget = document.createElement('div');
    widget.className = 'unified-feedback-toast';
    widget.dataset.messageType = toastData.messageType || 'unknown';

    // Add style-specific class
    widget.classList.add(toastStyle === 'bar' ? 'toast-bar' : 'toast-floating');

    // Simple message text (upload previews are handled by toast-preview-init.liquid)
    // Support both direct message and customTitle/customMessage pattern
    const title = toastData.customTitle || toastData.title || '';
    const messageText = toastData.customMessage || toastData.message || 'Your custom design is ready to add to cart.';
    const message = title ? `${title}\n${messageText}` : messageText;

    // For bar mode, create inner container with message and close button
    if (toastStyle === 'bar') {
      const innerContainer = document.createElement('div');
      innerContainer.className = 'toast-bar-inner';

      const messageSpan = document.createElement('span');
      messageSpan.className = 'toast-message';
      messageSpan.textContent = message;

      const closeBtn = document.createElement('button');
      closeBtn.className = 'toast-close-btn';
      closeBtn.innerHTML = '&times;';
      closeBtn.setAttribute('aria-label', 'Close notification');
      closeBtn.addEventListener('click', () => hideToast());

      innerContainer.appendChild(messageSpan);
      innerContainer.appendChild(closeBtn);
      widget.appendChild(innerContainer);
    } else {
      // Floating mode - simple text content
      widget.textContent = message;
    }

    // Add type-specific class for styling
    const typeClass = toastData.type || 'success';
    widget.classList.add(typeClass);

    // Insert bar mode after header-wrapper, floating mode to body
    if (toastStyle === 'bar') {
      const headerWrapper = document.querySelector('sticky-header, header-wrapper');
      if (headerWrapper && headerWrapper.parentNode) {
        headerWrapper.parentNode.insertBefore(widget, headerWrapper.nextSibling);
      } else {
        document.body.appendChild(widget);
      }
    } else {
      document.body.appendChild(widget);
    }
    log('✅ Toast widget created');

    // Auto-hide if configured
    if (TOAST_CONFIG.appearance.duration > 0) {
      setTimeout(() => {
        hideToast();
      }, TOAST_CONFIG.appearance.duration);
    }
  };

  // =====================================
  // DISPLAY FUNCTIONS
  // =====================================
    const showToast = (toastData = {}) => {
      if (!widget) {
        createWidget(toastData);
      }

      if (widget) {
        // FIXED: Add small delay to ensure element is rendered before animation
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            widget.classList.add('show');
            isActive = true;
            log('✅ Toast shown with animation');
          });
        });
      }
    };

  const hideToast = () => {
    if (widget) {
      widget.classList.remove('show');

      setTimeout(() => {
        if (widget && widget.parentNode) {
          widget.parentNode.removeChild(widget);
        }
        widget = null;
      }, 300);
    }
    isActive = false;
    currentProjectId = null;
    log('✅ Toast hidden');
  };

  // =====================================
  // ACTION HANDLERS
  // =====================================
  const editProject = () => {
    log('🎨 Edit project requested');
    
    if (currentProjectId) {
      // Find edit button and click it
      const editButton = document.querySelector('#pp_edit_btn');
      if (editButton && editButton.style.display !== 'none') {
        editButton.click();
        hideToast();
        return;
      }
      
      // Try to trigger PitchPrint edit mode
      if (window.ppclient && window.ppclient.fire) {
        window.ppclient.fire('edit-project', { projectId: currentProjectId });
        hideToast();
        return;
      }
    }
    
    log('⚠️ Could not trigger edit mode');
  };

  const clearProject = () => {
    log('🧹 Clear project requested');
    
    try {
      // Method 1: Use clear button
      const clearButton = document.querySelector('#pp_clear_design_btn');
      if (clearButton && clearButton.style.display !== 'none') {
        clearButton.click();
        hideToast();
        return;
      }
      
      // Method 2: Use PitchPrint API
      if (window.ppclient && window.ppclient.fire) {
        window.ppclient.fire('clear-design');
        hideToast();
        return;
      }
      
      // Method 3: Use StateManager
      if (stateManager) {
        stateManager.clearProject();
        hideToast();
        return;
      }
      
    } catch (error) {
      log('❌ Error clearing project:', error);
    }
  };

  const addToCart = () => {
    log('🛒 Add to cart requested');
    
    try {
      const addToCartButton = document.querySelector('.product-form__submit, button[name="add"], [data-unified-managed="true"]');
      if (addToCartButton) {
        addToCartButton.click();
        hideToast();
      } else {
        log('⚠️ Add to cart button not found');
      }
    } catch (error) {
      log('❌ Error adding to cart:', error);
    }
  };

  // =====================================
  // ACTIVATION FUNCTIONS
  // =====================================
  const activateToast = (data) => {
      if (!data) {
        log('⚠️ No data provided for toast activation');
        return;
      }

      // FIXED: Some message types don't need project IDs (like addToCartSuccess)
      const messageTypesWithoutProjectId = ['addToCartSuccess', 'errorOccurred', 'manualTrigger'];
      const requiresProjectId = !messageTypesWithoutProjectId.includes(data.messageType);

      if (requiresProjectId && !data.projectId) {
        log('⚠️ No project ID provided for toast activation (required for this message type)');
        return;
      }

      // Guard against duplicate activation (only for project-specific toasts)
      if (requiresProjectId && isActive && currentProjectId === data.projectId) {
        log('ℹ️ Toast already active for this project');
        return;
      }

      currentProjectId = data.projectId || null;
      log('🎯 Activating toast:', data.messageType, currentProjectId ? `for project: ${currentProjectId}` : '(no project ID)');
      
      showToast(data);
    };

  const deactivateToast = () => {
    log('❌ Deactivating toast');
    hideToast();
  };

  // =====================================
  // ENHANCED STATE MANAGER LISTENERS
  // =====================================

  // In your assets/toast-preview-system.js file
  // Find the setupPostReloadToast function and replace it with this enhanced version:

  const setupPostReloadToast = () => {
    // Check if we should show a post-reload success toast
    const shouldShowToast = sessionStorage.getItem('pitchprint_show_success_toast');
    const timestamp = sessionStorage.getItem('pitchprint_success_timestamp');
    
    log('🔍 Checking for post-reload toast flags:', {
      shouldShowToast,
      timestamp,
      hasTimestamp: !!timestamp
    });
    
    if (shouldShowToast === 'true' && timestamp) {
      const timeDiff = Date.now() - parseInt(timestamp);
      
      log('📊 Post-reload toast timing:', {
        timeDiff,
        maxAge: 10000,
        withinWindow: timeDiff < 10000
      });
      
      // Only show if the timestamp is recent (within 10 seconds)
      if (timeDiff < 10000) {
        log('📡 Post-reload: showing delayed addToCartSuccess toast');
        
        // Wait a moment for page to fully load
        setTimeout(() => {
          log('🎯 Triggering post-reload addToCartSuccess toast');
          showToastMessage('addToCartSuccess', {
            title: 'Added to Cart!',
            message: 'Your custom design has been added to cart.',
            type: 'success'
          });
        }, 1500);
      } else {
        log('📡 Post-reload: toast timestamp too old, skipping');
      }
      
      // Clear the flags regardless of whether we showed the toast
      sessionStorage.removeItem('pitchprint_show_success_toast');
      sessionStorage.removeItem('pitchprint_success_timestamp');
      log('🧹 Post-reload toast flags cleared');
    } else {
      log('📡 Post-reload: no toast flags found');
    }
  };

  const setupStateManagerListeners = () => {
    if (!stateManager) return;

    // NOTE: File upload completion is handled by toast-preview-init.liquid
    // which triggers the full interactive widget in the lower-left corner
    // This simple notification toast is NOT used for file uploads

    try {
      // CRITICAL: Remove any existing listeners first to prevent duplicates
      log('🧹 Cleaning up old listeners...');
      
      // Clear old listeners if they exist
      if (stateManager.off) {
        stateManager.off('project:changed');
        stateManager.off('project:restored'); 
        stateManager.off('project:cleared');
        stateManager.off('toast:activate');
        stateManager.off('toast:deactivate');
        log('✅ Old listeners removed');
      }

      // Project saved toast - ONLY if enabled in config
      if (TOAST_CONFIG.enabled && TOAST_CONFIG.messages.projectSaved) {
        stateManager.on('project:changed', (event) => {
          const projectData = event.detail;
          if (projectData && projectData.projectId) {
            log('📡 Project changed event - showing projectSaved toast');
            showToastMessage('projectSaved', {
              title: 'Design Saved!',
              message: 'Design saved! Ready to add to cart.',
              projectId: projectData.projectId
            });
          }
        });
        log('✅ Project saved listener attached');
      } else {
        log('🔇 Project saved listener SKIPPED (disabled in config)');
      }

      // Project restored toast - ONLY if enabled
      if (TOAST_CONFIG.enabled && TOAST_CONFIG.messages.projectRestored) {
        stateManager.on('project:restored', (event) => {
          const projectData = event.detail;
          log('📡 Project restored event - showing projectRestored toast');
          showToastMessage('projectRestored', {
            title: 'Design Restored!',
            message: 'Your previous design has been loaded.',
            projectId: projectData.projectId
          });
        });
        log('✅ Project restored listener attached');
      } else {
        log('🔇 Project restored listener SKIPPED (disabled in config)');
      }

      // Project cleared toast - ONLY if enabled
      if (TOAST_CONFIG.enabled && TOAST_CONFIG.messages.projectCleared) {
        stateManager.on('project:cleared', () => {
          log('📡 Project cleared event - showing projectCleared toast');
          showToastMessage('projectCleared', {
            title: 'Design Cleared',
            message: 'Start fresh with a new design.',
            type: 'info'
          });
        });
        log('✅ Project cleared listener attached');
      } else {
        log('🔇 Project cleared listener SKIPPED (disabled in config)');
      }

      // Add to cart success toast - ONLY if enabled
      if (TOAST_CONFIG.enabled && TOAST_CONFIG.messages.addToCartSuccess) {
        // Strategy: Show toast after page reload, not immediately
        setupPostReloadToast();
        
        // Listen for actual cart addition events (not cart opening)
        const cartEvents = ['cart:updated', 'cart:item-added'];
        
        cartEvents.forEach(eventName => {
          document.addEventListener(eventName, (e) => {
            log(`📡 ${eventName} event - flagging for post-reload toast`);
            
            // Set flag for post-reload toast instead of showing immediately
            sessionStorage.setItem('pitchprint_show_success_toast', 'true');
            sessionStorage.setItem('pitchprint_success_timestamp', Date.now().toString());
            
            log('✅ Post-reload toast flagged');
          });
        });
        
        log('✅ Add to cart success listener attached (post-reload strategy)');
      }

      // Manual activation - ONLY if enabled
      if (TOAST_CONFIG.enabled && TOAST_CONFIG.messages.manualTrigger) {
        stateManager.on('toast:activate', (event) => {
          const data = event.detail;
          log('📡 Manual toast activation');
          activateToast(data);
        });
        log('✅ Manual trigger listener attached');
      } else {
        log('🔇 Manual trigger listener SKIPPED (disabled in config)');
      }

      // Deactivation (always available for cleanup)
      stateManager.on('toast:deactivate', () => {
        log('📡 Toast deactivation');
        deactivateToast();
      });

      const activeTypes = getActiveMessageTypes();
      log(`📡 StateManager listeners setup complete - Active: [${activeTypes.join(', ')}]`);
      
      if (activeTypes.length === 0) {
        log('🔇 NO toast messages are enabled - system is effectively silent');
      }

    } catch (error) {
      log('❌ Error setting up StateManager listeners:', error);
    }
  };

  // =====================================
  // ENHANCED CONFIGURATION CHECKER
  // =====================================
  const debugConfiguration = () => {
    console.log('🔍 PitchPrint Toast Configuration Debug:');
    console.log('Master enabled:', TOAST_CONFIG.enabled);
    console.table(TOAST_CONFIG.messages);
    
    const enabledMessages = Object.entries(TOAST_CONFIG.messages)
      .filter(([type, enabled]) => enabled)
      .map(([type]) => type);
      
    console.log('Enabled message types:', enabledMessages);
    
    if (enabledMessages.length === 0) {
      console.log('🔇 ALL MESSAGES DISABLED - No toasts should appear');
    }
  };

  // =====================================
  // CONTROL FUNCTIONS
  // =====================================
  window.PitchPrintToastControl = {
    // Enable/disable specific message types
      enable: (messageType) => {
            if (TOAST_CONFIG.messages.hasOwnProperty(messageType)) {
              TOAST_CONFIG.messages[messageType] = true;
              console.log(`✅ Toast message '${messageType}' ENABLED`);
              
              // FIXED: Refresh listeners when enabling message types
              if (initializationComplete) {
                console.log('🔄 Refreshing listeners for newly enabled message type...');
                setupStateManagerListeners();
              }
            } else {
              console.log(`❌ Unknown message type: ${messageType}`);
              console.log('Available types:', Object.keys(TOAST_CONFIG.messages));
            }
          },

      disable: (messageType) => {
            if (TOAST_CONFIG.messages.hasOwnProperty(messageType)) {
              TOAST_CONFIG.messages[messageType] = false;
              console.log(`🔇 Toast message '${messageType}' DISABLED`);
            } else {
              console.log(`❌ Unknown message type: ${messageType}`);
            }
          },

    // Toggle specific message type
    toggle: (messageType) => {
      if (TOAST_CONFIG.messages.hasOwnProperty(messageType)) {
        TOAST_CONFIG.messages[messageType] = !TOAST_CONFIG.messages[messageType];
        const status = TOAST_CONFIG.messages[messageType] ? 'ENABLED' : 'DISABLED';
        console.log(`🔄 Toast message '${messageType}' ${status}`);
      }
    },

    // Bulk operations
    enableAll: () => {
      Object.keys(TOAST_CONFIG.messages).forEach(type => {
        TOAST_CONFIG.messages[type] = true;
      });
      console.log('✅ All toast messages ENABLED');
      
      // FIXED: Refresh listeners when enabling all message types
      if (initializationComplete) {
        console.log('🔄 Refreshing listeners for all enabled message types...');
        setupStateManagerListeners();
      }
    },

    disableAll: () => {
      Object.keys(TOAST_CONFIG.messages).forEach(type => {
        TOAST_CONFIG.messages[type] = false;
      });
      deactivateToast();
      console.log('🔇 All toast messages DISABLED');
      
      // FIXED: Refresh listeners when disabling all message types
      if (initializationComplete) {
        console.log('🔄 Refreshing listeners for all disabled message types...');
        setupStateManagerListeners();
      }
    },

    // Master controls
    masterEnable: () => {
      TOAST_CONFIG.enabled = true;
      console.log('🔊 Toast system ENABLED');
    },

    masterDisable: () => {
      TOAST_CONFIG.enabled = false;
      deactivateToast();
      console.log('🔇 Toast system DISABLED');
    },

    // Configuration
    getConfig: () => {
      console.log('📋 Current Toast Configuration:');
      console.table(TOAST_CONFIG.messages);
      console.log('Appearance:', TOAST_CONFIG.appearance);
      return TOAST_CONFIG;
    },

    setPosition: (position) => {
      const validPositions = ['top-right', 'top-left', 'bottom-right', 'bottom-left'];
      if (validPositions.includes(position)) {
        TOAST_CONFIG.appearance.position = position;
        console.log(`📍 Toast position set to: ${position}`);
      } else {
        console.log(`❌ Invalid position. Use: ${validPositions.join(', ')}`);
      }
    },

    setDuration: (seconds) => {
      TOAST_CONFIG.appearance.duration = seconds * 1000;
      console.log(`⏱️ Toast duration set to: ${seconds} seconds`);
    },

    // Utility functions
    listMessageTypes: () => {
      console.log('📝 Available message types:');
      Object.entries(TOAST_CONFIG.messages).forEach(([type, enabled]) => {
        console.log(`  ${type}: ${enabled ? '✅' : '❌'}`);
      });
    },

    // Debug and cleanup functions
    debug: () => {
      debugConfiguration();
    },

    forceCleanup: () => {
      // Remove any existing toast widgets
      document.querySelectorAll('.toast-preview-widget').forEach(el => {
        el.remove();
        console.log('🗑️ Removed toast widget');
      });
      
      // Reset widget state
      widget = null;
      isActive = false;
      currentProjectId = null;
      
      console.log('🧹 Force cleanup completed');
    },

    testConfiguration: () => {
      console.log('🧪 Testing current configuration...');
      debugConfiguration();
      
      // Test if listeners are properly conditional
      const activeTypes = Object.entries(TOAST_CONFIG.messages)
        .filter(([type, enabled]) => enabled)
        .map(([type]) => type);
        
      if (activeTypes.length === 0) {
        console.log('✅ Configuration is correct - no messages should appear');
      } else {
        console.log('⚠️ These messages are enabled:', activeTypes);
      }
    },

    refreshListeners: () => {
      console.log('🔄 Refreshing listeners with current configuration...');
      setupStateManagerListeners();
      console.log('✅ Listeners refreshed');
    },

    // Quick presets
    presets: {
      silent: () => {
        window.PitchPrintToastControl.disableAll();
        console.log('🤫 Preset: SILENT MODE activated');
      },
      
      essentials: () => {
        window.PitchPrintToastControl.disableAll();
        window.PitchPrintToastControl.enable('projectSaved');
        window.PitchPrintToastControl.enable('errorOccurred');
        console.log('⚡ Preset: ESSENTIALS activated (only saved projects and errors)');
      },
      
      full: () => {
        window.PitchPrintToastControl.enableAll();
        console.log('🎯 Preset: FULL MODE activated');
      },
      
      production: () => {
        window.PitchPrintToastControl.disableAll();
        window.PitchPrintToastControl.enable('manualTrigger');
        console.log('🏭 Preset: PRODUCTION MODE activated (manual only)');
      }
    }
  };
  // =====================================
  // INJECT TOAST STYLES
  // =====================================
    const injectToastStyles = () => {
      if (document.getElementById('unified-toast-styles')) return;

      const styles = document.createElement('style');
      styles.id = 'unified-toast-styles';
      styles.textContent = `
        /* ===================================== */
        /* FLOATING MODE (Original Style)        */
        /* ===================================== */
        .unified-feedback-toast.toast-floating {
          position: fixed;
          top: 90px;
          right: 20px;
          z-index: 9999;
          padding: 12px 16px;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0.85);
          color: white;
          font-size: 14px;
          font-weight: 500;
          transform: translateX(400px);
          opacity: 0;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          border-left: 4px solid #6f08fb;
          max-width: 300px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .unified-feedback-toast.toast-floating.show {
          transform: translateX(0);
          opacity: 1;
        }

        /* ===================================== */
        /* BAR MODE (New Full-Width Style)       */
        /* ===================================== */
        .unified-feedback-toast.toast-bar {
          position: relative;
          top: 0;
          left: 0;
          right: 0;
          width: 100%;
          z-index: 10;
          max-height: 0;
          overflow: hidden;
          padding: 0;
          border-radius: 0;
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%);
          border: 2px solid transparent;
          background-clip: padding-box;
          color: #7c3aed;
          font-size: 14px;
          font-weight: 500;
          opacity: 1;
          transition: max-height 0.4s cubic-bezier(0.4, 0.0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0.0, 0.2, 1);
          box-shadow: 0 4px 12px rgba(168, 85, 247, 0.15);
        }

        .unified-feedback-toast.toast-bar::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 2px 0;
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          pointer-events: none;
          z-index: -1;
        }

        .unified-feedback-toast.toast-bar.show {
          max-height: 200px;
          padding: 16px 0;
        }

        /* Inner container with max-width */
        .unified-feedback-toast.toast-bar .toast-bar-inner {
          max-width: var(--page-width, 120rem);
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .unified-feedback-toast.toast-bar .toast-close-btn {
          background: none;
          border: none;
          color: #7c3aed;
          font-size: 24px;
          line-height: 1;
          cursor: pointer;
          padding: 0;
          margin-left: 16px;
          opacity: 0.6;
          transition: opacity 0.2s ease, transform 0.2s ease;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .unified-feedback-toast.toast-bar .toast-close-btn:hover {
          opacity: 1;
          transform: scale(1.1);
        }

        .unified-feedback-toast.toast-bar .toast-message {
          flex: 1;
        }

        /* ===================================== */
        /* COLOR VARIANTS                        */
        /* ===================================== */

        /* Floating mode - left border colors */
        .unified-feedback-toast.toast-floating.success {
          border-left-color: #6f08fb;
        }
        .unified-feedback-toast.toast-floating.error {
          border-left-color: #ef4444;
        }
        .unified-feedback-toast.toast-floating.warning {
          border-left-color: #f59e0b;
        }
        .unified-feedback-toast.toast-floating.info {
          border-left-color: #3b82f6;
        }

        /* Bar mode - gradient border colors */
        .unified-feedback-toast.toast-bar.success::after {
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
        }
        .unified-feedback-toast.toast-bar.success {
          background: linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%);
          color: #7c3aed;
        }

        .unified-feedback-toast.toast-bar.error::after {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }
        .unified-feedback-toast.toast-bar.error {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
          color: #dc2626;
        }
        .unified-feedback-toast.toast-bar.error .toast-close-btn {
          color: #dc2626;
        }

        .unified-feedback-toast.toast-bar.warning::after {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        }
        .unified-feedback-toast.toast-bar.warning {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
          color: #d97706;
        }
        .unified-feedback-toast.toast-bar.warning .toast-close-btn {
          color: #d97706;
        }

        .unified-feedback-toast.toast-bar.info::after {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }
        .unified-feedback-toast.toast-bar.info {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
          color: #2563eb;
        }
        .unified-feedback-toast.toast-bar.info .toast-close-btn {
          color: #2563eb;
        }

        /* ===================================== */
        /* MOBILE RESPONSIVENESS                 */
        /* ===================================== */
        @media (max-width: 768px) {
          .unified-feedback-toast.toast-floating {
            top: 80px;
            right: 10px;
            left: 10px;
            transform: translateY(-100px);
            max-width: none;
          }

          .unified-feedback-toast.toast-floating.show {
            transform: translateY(0);
          }

          .unified-feedback-toast.toast-bar .toast-bar-inner {
            padding: 0 1.5rem;
          }
        }
      `;
      document.head.appendChild(styles);
      log('🎨 Toast styles injected');
    };

  // =====================================
  // INITIALIZATION
  // =====================================
    const init = () => {
      if (initializationComplete) {
        log('Already initialized, skipping...');
        return;
      }
      initializationComplete = true;

      log('🎯 Initializing toast system (product page detected)...');
      
      try {
        injectToastStyles();
        setupStateManagerListeners();
        log('📡 StateManager listeners attached');
        log('✅ Toast system ready');
        log('🎨 Toast Preview system loaded (LOOP FIXED)');
      } catch (error) {
        log('❌ Error during initialization:', error);
      }
    };

  // =====================================
  // PUBLIC API
  // =====================================
  window.ToastPreviewStateManager = {
    isActive: () => isActive,
    getProjectId: () => currentProjectId,
    activate: activateToast,
    deactivate: deactivateToast
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }

})();